/**
 * npc_dialogue.ts — PURE salience dialogue for NPCs (Valve rule-database lite,
 * per the research digest: facts dictionary → rules with criteria → the rule
 * matching the MOST criteria wins; deterministic shuffle-bag variety; template
 * %fills%). This is the TRADITIONAL dialogue layer — instant, free, offline.
 * Free-text questions escalate to the LLM inbox (sayToVersona) instead.
 */

export interface DialogueFacts {
  topic: 'greet' | 'work' | 'town' | 'news';
  job: string;            // farmer | miner | smith | merchant | guard | bard | scout
  state: string;          // working | resting | patrolling | idle
  archetype: string;
  tod: 'morning' | 'day' | 'evening' | 'night';
  hungry: boolean;
  happy: boolean;         // mood >= 60
  grumpy: boolean;        // mood <= 35
  hasNews: boolean;
  newsDetail: string;     // latest npc_event detail ('' when none)
  foodPrice: number;      // whole COINS (the simple money system)
  project: string;        // settlement project label ('' when none)
  playerName: string;
  isMayor: boolean;
  taxPct: number;         // settlement tax as a whole percent
  custom: string;         // newest active settlement norm text ('' when none)
  memoryLine: string;     // the NPC's most important memory ('' when none)
  /** Dominant typed-emotion tone ('' calm) + its human-readable cause. */
  feelingTone: 'angry' | 'glad' | 'uneasy' | 'down' | '';
  feelingCause: string;
  /** Civilization identity (settlement structure pack): '' when unknown. */
  civName: string;        // 'Aurelian Concord' | 'Borean Compact' | ''
  civDemonym: string;     // 'Aurelian' | 'Borean' | ''
  /** Whether the PLAYER last talked to this civ's folk... reserved; template salience keys off civDemonym. */
  workplace: string;      // 'the Aurelian ore works' ('' when unknown)
}

interface Rule { criteria: Array<(f: DialogueFacts) => boolean>; lines: string[] }

const is = <K extends keyof DialogueFacts>(k: K, v: DialogueFacts[K]) => (f: DialogueFacts): boolean => f[k] === v;

const RULES: Rule[] = [
  // ── greet ──────────────────────────────────────────────────────────────────
  { criteria: [is('topic', 'greet')], lines: ['Hey there, %player%.', 'Well met.', 'Need something?'] },
  { criteria: [is('topic', 'greet'), is('tod', 'night')], lines: ['Late to be out, %player%.', 'Quiet night, huh.'] },
  { criteria: [is('topic', 'greet'), is('grumpy', true)], lines: ['What.', 'Make it quick, %player%.'] },
  { criteria: [is('topic', 'greet'), is('happy', true)], lines: ['Good to see you, %player%!', 'Fine day, isn\'t it?'] },
  { criteria: [is('topic', 'greet'), is('isMayor', true)], lines: ['Mayor\'s office is always open, %player%.', 'A word with your mayor? Go on.'] },
  { criteria: [is('civDemonym', 'Aurelian')], lines: ['The Concord keeps its word, %player%.', 'You stand in Aurelian streets — mind the spires.'] },
  { criteria: [is('civDemonym', 'Borean')], lines: ['The Compact provides, %player%.', 'Borean towers, Borean pride.'] },
  { criteria: [is('topic', 'greet'), is('feelingTone', 'angry')], lines: ['Not the best time, %player%.', 'Careful — I\'m in no mood today.'] },
  { criteria: [is('topic', 'greet'), is('feelingTone', 'glad')], lines: ['%player%! Good day for it, whatever it is.', 'You caught me on a fine day.'] },
  { criteria: [is('topic', 'greet'), is('feelingTone', 'uneasy')], lines: ['Oh — %player%. You startled me.', 'Keep your voice down, would you?'] },
  // ── work ───────────────────────────────────────────────────────────────────
  { criteria: [is('topic', 'work')], lines: ['I keep busy. %job% work, mostly.', 'The %job% trade keeps this town fed one way or another.'] },
  { criteria: [is('topic', 'work'), is('job', 'farmer')], lines: ['Crops don\'t tend themselves. Food sells at %foodPrice% at the market.', 'Soil\'s stubborn out here, but it grows.'] },
  { criteria: [is('topic', 'work'), is('job', 'miner')], lines: ['Ore\'s down there if your back can take it.', 'Haul ore in, sell it, buy dinner. That\'s the shape of it.'] },
  { criteria: [is('topic', 'work'), is('job', 'smith')], lines: ['I turn the miners\' ore into tools. No ore, no tools.', 'Forge work. The town needs tools more than it knows.'] },
  { criteria: [is('topic', 'work'), is('job', 'scout')], lines: ['I walk the far ground so you don\'t have to.', 'Been out past the ridge today. Map\'s never done.'] },
  { criteria: [is('topic', 'work'), is('job', 'guard')], lines: ['I watch. Mostly nothing happens. Mostly.', 'Someone has to keep the peace on market day.'] },
  { criteria: [is('topic', 'work'), is('job', 'bard')], lines: ['Songs are goods too, friend — luxury line, top shelf.', 'I play evenings at the tavern. Come by.'] },
  { criteria: [is('topic', 'work'), is('state', 'working')], lines: ['As you can see — mid-shift.', 'Can\'t chat long, work\'s not done.'] },
  { criteria: [is('topic', 'work'), is('hungry', true)], lines: ['Hard to work on an empty stomach.', 'I\'ll do better after a meal, honestly.'] },
  // ── town ───────────────────────────────────────────────────────────────────
  { criteria: [is('topic', 'town')], lines: ['Small place, but it\'s ours.', 'Market\'s at the center. Food runs %foodPrice% these days.'] },
  { criteria: [is('topic', 'town'), (f) => f.project !== '' ], lines: ['Town\'s building %project% — treasury money at work.', 'They broke ground on %project%. About time.'] },
  { criteria: [is('topic', 'town'), (f) => f.foodPrice > 8], lines: ['Food\'s dear lately — %foodPrice% a basket. Farmers can\'t keep up.', 'Prices are up. Bring grain if you have it.'] },
  { criteria: [is('topic', 'town'), (f) => f.foodPrice < 3], lines: ['Food\'s cheap as dirt right now. Good harvests.', 'Market\'s flooded with grain — buyer\'s day.'] },
  { criteria: [is('topic', 'town'), (f) => f.taxPct >= 8], lines: ['Tax runs %tax% under this mayor. Feels heavier every market day.', 'The levy\'s at %tax%. Somebody\'s fountain better be worth it.'] },
  { criteria: [is('topic', 'town'), (f) => f.custom !== ''], lines: ['Way we do things here: %custom%', 'New folks learn quick — %custom%'] },
  { criteria: [is('topic', 'town'), (f) => f.custom !== '', is('grumpy', true)], lines: ['They keep preaching "%custom%" Like that fixes anything.'] },
  // ── news ───────────────────────────────────────────────────────────────────
  { criteria: [is('topic', 'news'), is('hasNews', false)], lines: ['Quiet lately. Suits me.', 'No news is good news out here.'] },
  { criteria: [is('topic', 'news'), is('hasNews', true)], lines: ['Word is: %news%.', 'You didn\'t hear it from me — %news%.', 'Talk of the town: %news%.'] },
  { criteria: [is('topic', 'news'), is('hasNews', false), (f) => f.memoryLine !== ''], lines: ['Me? %memory%', 'Nothing new in town. Though — %memory%'] },
  { criteria: [is('topic', 'news'), (f) => f.feelingCause !== '', (f) => f.feelingTone !== ''], lines: ['Honestly? %feelingCause% — that\'s what\'s on my mind.', 'Ask the others about news. Me, %feelingCause%.'] },
];

/** Most-criteria-matched rule wins; ties by rule order; line by shuffle-bag hash. */
export function pickLine(f: DialogueFacts, bag: number): string {
  let best: Rule | null = null;
  let bestN = -1;
  for (const r of RULES) {
    let n = 0;
    let ok = true;
    for (const c of r.criteria) { if (c(f)) n++; else { ok = false; break; } }
    if (ok && n > bestN) { best = r; bestN = n; }
  }
  if (!best) return '…';
  const line = best.lines[Math.abs(bag) % best.lines.length];
  return fill(line, f);
}

function fill(line: string, f: DialogueFacts): string {
  return line
    .replace(/%player%/g, f.playerName || 'stranger')
    .replace(/%job%/g, f.job)
    .replace(/%foodPrice%/g, `${f.foodPrice} coin${f.foodPrice === 1 ? '' : 's'}`)
    .replace(/%project%/g, f.project || 'something')
    .replace(/%news%/g, f.newsDetail || 'nothing much')
    .replace(/%tax%/g, `${f.taxPct}%`)
    .replace(/%custom%/g, f.custom)
    .replace(/%memory%/g, f.memoryLine)
    .replace(/%feelingCause%/g, f.feelingCause);
}

/** Bucket an OCC episode type into a dialogue tone. */
export function feelingToneOf(etype: string): DialogueFacts['feelingTone'] {
  if (etype === 'anger' || etype === 'resentment' || etype === 'reproach') return 'angry';
  if (etype === 'joy' || etype === 'satisfaction' || etype === 'relief' || etype === 'gratification' || etype === 'gratitude' || etype === 'pride' || etype === 'hope' || etype === 'happy_for' || etype === 'gloating') return 'glad';
  if (etype === 'fear' || etype === 'fears_confirmed') return 'uneasy';
  if (etype === 'distress' || etype === 'disappointment' || etype === 'shame' || etype === 'remorse' || etype === 'pity') return 'down';
  return '';
}

export function todBucket(tod: number): DialogueFacts['tod'] {
  const t = ((tod % 1) + 1) % 1;
  if (t < 0.28) return 'night';
  if (t < 0.45) return 'morning';
  if (t < 0.72) return 'day';
  if (t < 0.9) return 'evening';
  return 'night';
}
